import { useMemo, useState, type FormEvent } from 'react';
import {
  AUTH_PORTAL_CLARUS_LOGIN_URL,
  CHECKOUT_COMPLETION_REDIRECT_TARGET,
  type SignupOfferDisplayDto,
} from './config/signupOffers';
import { submitEnrollment, type SubmitEnrollmentInputDto } from './enrollment/client';

export interface SignupAppProps {
  offers: SignupOfferDisplayDto[];
  currentUrl?: string;
  submitEnrollmentImpl?: SubmitEnrollmentFn;
}

type SubmitEnrollmentFn = (input: SubmitEnrollmentInputDto) => Promise<{ submissionId: string; message: string }>;

export function SignupApp({
  offers,
  currentUrl = window.location.href,
  submitEnrollmentImpl = submitEnrollment,
}: SignupAppProps) {
  const url = useMemo(() => new URL(currentUrl), [currentUrl]);
  const isEnrollmentRoute = url.pathname === '/intake/enrollment';
  const checkout = url.searchParams.get('checkout');
  const sessionId = url.searchParams.get('session_id');

  if (url.pathname === '/welcome') return <GoneWelcomeRoute />;
  if (isEnrollmentRoute) return <EnrollmentRoute submitEnrollmentImpl={submitEnrollmentImpl} />;
  if (checkout === 'completed' && sessionId) return <CheckoutReceipt sessionId={sessionId} />;
  return <OfferList offers={offers} />;
}

function OfferList({ offers }: { offers: SignupOfferDisplayDto[] }) {
  return (
    <main className="signup-shell" aria-label="Darice sign-up">
      <header className="signup-hero">
        <p className="signup-kicker">Darice Strategies</p>
        <h1>Choose a Darice starting point</h1>
        <p>Buy through Stripe, then sign in with the checkout email after provisioning completes.</p>
      </header>

      <section className="offer-grid" aria-label="Configured offers">
        {offers.length === 0 ? (
          <article className="notice" role="status">
            Sign-up offers are not configured for this build.
          </article>
        ) : offers.map((offer) => (
          <article className="offer-card" key={offer.routeMetadata.offerRouteKey}>
            <div>
              <h2>{offer.title}</h2>
              <p className="price-label">{offer.priceLabel}</p>
              <p>{offer.description}</p>
            </div>
            <a
              className="primary-action"
              href={offer.paymentLinkUrl}
              data-offer-route-key={offer.routeMetadata.offerRouteKey}
              data-destination-app={offer.routeMetadata.destinationApp}
            >
              Buy
            </a>
          </article>
        ))}
      </section>

      <p className="completion-target">Stripe completion target: {CHECKOUT_COMPLETION_REDIRECT_TARGET}</p>
    </main>
  );
}

function CheckoutReceipt({ sessionId }: { sessionId: string }) {
  return (
    <main className="signup-shell receipt-shell" aria-label="Checkout receipt">
      <p className="signup-kicker">Checkout received</p>
      <h1>Finish by signing in</h1>
      <p>
        Stripe returned receipt reference <span data-testid="receipt-session-id">{sessionId}</span>.
        It does not grant access or identify a workspace.
      </p>
      <a className="primary-action" href={AUTH_PORTAL_CLARUS_LOGIN_URL}>Sign in to Clarus</a>
    </main>
  );
}

function EnrollmentRoute({
  submitEnrollmentImpl,
}: {
  submitEnrollmentImpl: SubmitEnrollmentFn;
}) {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'complete' | 'failed'>('idle');
  const [message, setMessage] = useState<string>('');

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus('submitting');
    setMessage('');
    const form = new FormData(event.currentTarget);
    try {
      const input: SubmitEnrollmentInputDto = {
        name: form.get('name')?.toString() ?? '',
        email: form.get('email')?.toString() ?? '',
        sourceUrl: 'https://signup.daricestrategies.com/intake/enrollment',
      };
      addOptionalFormValue(input, form, 'companyName');
      addOptionalFormValue(input, form, 'phone');
      addOptionalFormValue(input, form, 'offerRouteKey');
      addOptionalFormValue(input, form, 'message');
      const result = await submitEnrollmentImpl(input);
      setStatus('complete');
      setMessage(`Enrollment received: ${result.submissionId}`);
    } catch (error) {
      setStatus('failed');
      setMessage(error instanceof Error ? error.message : 'FAIL: enrollment submission failed');
    }
  }

  return (
    <main className="signup-shell" aria-label="Darice enrollment">
      <header className="signup-hero">
        <p className="signup-kicker">Enrollment</p>
        <h1>Tell Darice where to start</h1>
      </header>
      <form className="enrollment-form" onSubmit={onSubmit}>
        <label>Name<input name="name" required /></label>
        <label>Email<input name="email" type="email" required /></label>
        <label>Company<input name="companyName" /></label>
        <label>Phone<input name="phone" /></label>
        <label>Offer route key<input name="offerRouteKey" /></label>
        <label>Message<textarea name="message" rows={5} /></label>
        <button className="primary-action" type="submit" disabled={status === 'submitting'}>
          {status === 'submitting' ? 'Submitting' : 'Submit'}
        </button>
      </form>
      {message ? <p role={status === 'failed' ? 'alert' : 'status'}>{message}</p> : null}
    </main>
  );
}

function addOptionalFormValue(
  input: SubmitEnrollmentInputDto,
  form: FormData,
  field: 'companyName' | 'phone' | 'offerRouteKey' | 'message'
): void {
  const value = form.get(field)?.toString().trim();
  if (value) input[field] = value;
}

function GoneWelcomeRoute() {
  return (
    <main className="signup-shell receipt-shell" aria-label="Inactive welcome route">
      <h1>This link is inactive</h1>
      <p>Darice sign-up now uses Stripe checkout completion followed by normal email-code sign-in.</p>
      <a className="primary-action" href={AUTH_PORTAL_CLARUS_LOGIN_URL}>Sign in</a>
    </main>
  );
}
